import { type FC, type ReactNode } from 'react';
import {
    LuImage, LuVideo, LuFileText, LuMapPin, LuExternalLink, LuPhone, LuReply,
    LuCopy, LuWorkflow, LuMessageSquare,
} from 'react-icons/lu';
import { getMidiaUrl } from '../services/GerenciarEmpresaService';
import './TemplateBubble.css';

/**
 * A mensagem do template como ela chega no WhatsApp.
 *
 * Duas telas desenham isto — a listagem (visão bloco) e a página do
 * template —, e o Disparador provavelmente vai querer também. Por isso é
 * componente e não JSX copiado: a bolha é a leitura mais importante da
 * tela e não pode divergir entre os dois lugares.
 *
 * O componente NÃO sabe ler a API: quem traduz o `componentesJson` da
 * Meta para cá é `lerComponentes()`, exportada abaixo. A página de edição
 * monta o `TemplateVisual` direto do formulário, sem passar por JSON.
 */

export type FormatoHeader = 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';

export interface BotaoVisual {
    tipo: string;
    texto: string;
}

export interface TemplateVisual {
    formatoHeader?: FormatoHeader;
    /** Só quando `formatoHeader` é `TEXT`. */
    textoHeader?: string;
    textoBody: string;
    textoFooter?: string;
    botoes: BotaoVisual[];
    /** Na ordem de `{{1}}`, `{{2}}`… Usados quando `resolver` é true. */
    exemplos?: string[];
    /** A imagem do cabeçalho: a miniatura logo depois do upload, ou a cópia
     *  que a API guarda (a Meta devolve handle, não arquivo). */
    midiaUrl?: string;
}

interface TemplateBubbleProps {
    visual: TemplateVisual;
    /**
     * `true` troca `{{1}}` pelo exemplo (é o que a pessoa vai receber);
     * `false` mostra a variável destacada (é o que a Meta aprova).
     * A página usa resolvido, a listagem usa cru — quem lê a lista precisa
     * saber quantos campos vai preencher no disparo.
     */
    resolver?: boolean;
    /** Bloco da listagem: fonte menor, mídia baixa, corpo com teto de 4 linhas. */
    compact?: boolean;
    /** Teto de botões desenhados; o resto vira "+N botões". */
    maxBotoes?: number;
    /** Respiro maior — usado dentro da moldura de telefone. */
    roomy?: boolean;
    /** Texto quando não há nada para mostrar ainda. */
    vazio?: ReactNode;
}

const MEDIA_ICON: Record<string, ReactNode> = {
    IMAGE: <LuImage size={18} />,
    VIDEO: <LuVideo size={18} />,
    DOCUMENT: <LuFileText size={18} />,
    LOCATION: <LuMapPin size={18} />,
};

const MEDIA_LABEL: Record<string, string> = {
    IMAGE: 'Imagem',
    VIDEO: 'Vídeo',
    DOCUMENT: 'Documento',
    LOCATION: 'Localização',
};

const botaoIcone = (tipo: string) => {
    if (tipo === 'URL') return <LuExternalLink size={13} />;
    if (tipo === 'PHONE_NUMBER' || tipo === 'VOICE_CALL') return <LuPhone size={13} />;
    if (tipo === 'COPY_CODE') return <LuCopy size={13} />;
    if (tipo === 'FLOW') return <LuWorkflow size={13} />;
    return <LuReply size={13} />;
};

/**
 * Corta o texto nos `{{n}}` e devolve os pedaços — resolvidos pelo
 * exemplo, ou marcados como variável.
 *
 * `split` com grupo de captura mantém o número na lista, então os índices
 * ímpares são as variáveis. Assim não se perde nada do texto original
 * (que é `pre-line`: o parágrafo do corpo importa).
 */
export const partesDoTexto = (texto: string, exemplos: string[] | undefined, resolver: boolean): ReactNode[] =>
    texto.split(/\{\{(\d+)\}\}/g).map((pedaco, i) => {
        if (i % 2 === 0) return pedaco;
        const valor = exemplos?.[Number(pedaco) - 1];
        // Resolvendo sem exemplo preenchido, a variável continua aparecendo
        // crua: some melhor que virar buraco no meio da frase.
        if (resolver && valor) return valor;
        return <span key={`v${i}`} className="tpl-bub-var">{`{{${pedaco}}}`}</span>;
    });

/**
 * Traduz o `componentesJson` que a API guarda (formato da Meta) para cá.
 * A imagem não está no JSON — lá só há o handle —, por isso o caminho da
 * cópia local vem à parte (`caminhoMidiaHeader` do template).
 */
export const lerComponentes = (componentesJson: string | null, caminhoMidia?: string | null): TemplateVisual => {
    const vazio: TemplateVisual = { textoBody: '', botoes: [] };
    if (!componentesJson) return vazio;

    let comps: Array<Record<string, unknown>> = [];
    try {
        const lido = JSON.parse(componentesJson);
        if (!Array.isArray(lido)) return vazio;
        comps = lido;
    } catch {
        // JSON quebrado não derruba a listagem: o template aparece sem prévia.
        return vazio;
    }

    const achar = (tipo: string) => comps.find(c => c?.type === tipo);
    const header = achar('HEADER');
    const body = achar('BODY');
    const footer = achar('FOOTER');
    const buttons = achar('BUTTONS');

    const exemploBody = (body?.example as { body_text?: string[][] } | undefined)?.body_text?.[0];

    return {
        formatoHeader: header?.format as FormatoHeader | undefined,
        textoHeader: (header?.text as string | undefined) ?? undefined,
        textoBody: (body?.text as string | undefined) ?? '',
        textoFooter: (footer?.text as string | undefined) ?? undefined,
        botoes: ((buttons?.buttons as Array<Record<string, unknown>> | undefined) ?? []).map(b => ({
            tipo: (b?.type as string) ?? 'QUICK_REPLY',
            texto: (b?.text as string) ?? '',
        })),
        exemplos: Array.isArray(exemploBody) ? exemploBody : undefined,
        midiaUrl: caminhoMidia ? getMidiaUrl(caminhoMidia) : undefined,
    };
};

/** Quantas variáveis `{{n}}` distintas o corpo tem. */
export const contarVariaveis = (texto: string): number => {
    const achadas = new Set<string>();
    for (const m of texto.matchAll(/\{\{(\d+)\}\}/g)) achadas.add(m[1]);
    return achadas.size;
};

const TemplateBubble: FC<TemplateBubbleProps> = ({
    visual, resolver = false, compact = false, maxBotoes, roomy = false, vazio,
}) => {
    const { formatoHeader, textoHeader, textoBody, textoFooter, botoes, exemplos, midiaUrl } = visual;

    const temMidia = !!formatoHeader && formatoHeader !== 'TEXT';
    const temImagem = !!midiaUrl && formatoHeader === 'IMAGE';
    const temHeaderTexto = formatoHeader === 'TEXT' && !!textoHeader?.trim();
    const temAlgo = !!textoBody.trim() || temMidia || temHeaderTexto || botoes.length > 0;

    const classe = [
        'tpl-bub-wrap',
        compact ? 'is-compact' : '',
        roomy ? 'is-roomy' : '',
    ].filter(Boolean).join(' ');

    if (!temAlgo) {
        return (
            <div className={classe}>
                <div className="tpl-bub-vazio">
                    <LuMessageSquare size={20} aria-hidden="true" />
                    <span>{vazio ?? 'Escreva o corpo da mensagem para ver a prévia.'}</span>
                </div>
            </div>
        );
    }

    const desenhados = maxBotoes ? botoes.slice(0, maxBotoes) : botoes;
    const escondidos = botoes.length - desenhados.length;

    return (
        <div className={classe}>
            <div className="tpl-bub">
                {temMidia && (
                    <div className={`tpl-bub-media ${temImagem ? 'has-img' : ''}`}>
                        {temImagem
                            ? <img src={midiaUrl} alt="" />
                            : <>
                                {MEDIA_ICON[formatoHeader] ?? <LuImage size={18} />}
                                <span>{MEDIA_LABEL[formatoHeader] ?? 'Mídia'}</span>
                            </>}
                    </div>
                )}

                {temHeaderTexto && (
                    <div className="tpl-bub-htxt">
                        {partesDoTexto(textoHeader ?? '', exemplos, resolver)}
                    </div>
                )}

                {!!textoBody.trim() && (
                    <div className="tpl-bub-body">{partesDoTexto(textoBody, exemplos, resolver)}</div>
                )}

                {!!textoFooter?.trim() && <div className="tpl-bub-foot">{textoFooter}</div>}

                <div className="tpl-bub-hr">09:41</div>
            </div>

            {desenhados.length > 0 && (
                <div className="tpl-bub-btns">
                    {desenhados.map((b, i) => (
                        <div key={i} className="tpl-bub-btn">
                            {botaoIcone(b.tipo)}
                            <span>{b.texto || 'Botão'}</span>
                        </div>
                    ))}
                </div>
            )}

            {escondidos > 0 && (
                <p className="tpl-bub-mais">
                    +{escondidos} {escondidos === 1 ? 'botão' : 'botões'}
                </p>
            )}
        </div>
    );
};

export default TemplateBubble;
